import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import {
  ArrowLeft, TrendingUp, TrendingDown, Target, Lock,
  Sparkles, Shield, AlertTriangle, Loader2, Plus,
  RotateCcw, Calculator,
} from 'lucide-react';
import { Footer } from '../components/LegalDisclaimers';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { RendPlexLogo } from '../components/RendPlexLogo';
import DarkModeToggle from '../components/DarkModeToggle';
import AIAvatar from '../components/AIAvatar';
import {
  buildScenario, cloneScenario,
  DOWN_PAYMENT_PRESETS, TARGET_CASH_FLOW_PRESETS, SOLVE_FOR_OPTIONS,
} from '../components/scenario/scenarioAssumptions';
import FinancialImpact from '../components/scenario/FinancialImpact';
import BreakEvenPanel from '../components/scenario/BreakEvenPanel';
import ScenarioSlots from '../components/scenario/ScenarioSlots';
import BestCombination from '../components/scenario/BestCombination';
import SensitivityBars from '../components/scenario/SensitivityBars';
import SolverResult from '../components/scenario/SolverResult';
import DecisionIntelligence from '../components/scenario/DecisionIntelligence';
import { AnimatedNumber } from '../components/scenario/controls/AnimatedNumber';
import { VariableRow } from '../components/scenario/controls/VariableRow';
import { LockToggle } from '../components/scenario/controls/LockToggle';
import { NumericField } from '../components/scenario/controls/NumericField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => (Number.isFinite(n) ? Math.round(n).toLocaleString() : '0');
const fmtPct = (n, dec = 2) => (Number.isFinite(n) ? n.toFixed(dec) : '0.00');
const fmtMoney = (n) => `$${fmt(n)}`;

// ─── Main page ────────────────────────────────────────────────────────────
export default function ScenarioAnalyzer() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);

  // Original Deal snapshot — captured ONCE per hydration. Never mutated.
  const originalDealRef = useRef(null);
  const [originalDealReady, setOriginalDealReady] = useState(false);

  // Current Scenario — mutable working copy.
  const [scenario, setScenario] = useState(() => {
    const preload = location.state
      ? { purchase_price: location.state.purchasePrice, interest_rate: location.state.interestRate,
          annual_income: location.state.annualRent, down_payment_percent: location.state.downPaymentPercent }
      : null;
    const built = buildScenario(preload);
    originalDealRef.current = Object.freeze({ ...built.scenario });
    return built.scenario;
  });

  // Initialise "original" once (freehand mode is already handled above).
  useEffect(() => { setOriginalDealReady(true); }, []);

  // Locks
  const [locks, setLocks] = useState({
    purchase_price: false, interest_rate: false, rent: false, down_payment: false,
  });

  // Target cash flow
  const [targetCashFlow, setTargetCashFlow] = useState(500);

  // Solver state
  const [solveFor, setSolveFor] = useState('purchase_price');
  const [solverResult, setSolverResult] = useState(null);
  const [solving, setSolving] = useState(false);

  // Canonical metrics from /api/scenario/calculate
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const calcTimer = useRef(null);

  // Original Deal metrics — computed ONCE via canonical /calculate
  // whenever a new Original Deal is captured. Used exclusively by
  // Financial Impact so both sides of the delta come from the same
  // deterministic engine.
  const [originalMetrics, setOriginalMetrics] = useState(null);
  const [originalMetricsLoading, setOriginalMetricsLoading] = useState(false);

  // ─── Hydration: load deal from ?deal_id= ────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dealId = params.get('deal_id');
    if (!dealId) return;
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/deals/${dealId}`, { withCredentials: true });
        const built = buildScenario(resp.data);
        originalDealRef.current = Object.freeze({ ...built.scenario });
        setScenario(built.scenario);
        setOriginalMetrics(null); // triggers Original /calculate below
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ─── Canonical Original Deal metrics (fires once per hydration) ────────
  // Runs the immutable Original Deal through /api/scenario/calculate so
  // Financial Impact always compares apples-to-apples with the same
  // deterministic engine. Never touches Current Scenario or the DB.
  useEffect(() => {
    if (!isPro) return;
    if (originalMetrics) return;
    if (!originalDealRef.current) return;
    let cancelled = false;
    (async () => {
      setOriginalMetricsLoading(true);
      try {
        const resp = await axios.post(
          `${BACKEND_URL}/api/scenario/calculate`,
          { scenario: originalDealRef.current },
          { withCredentials: true }
        );
        if (!cancelled) setOriginalMetrics(resp.data.metrics);
      } catch { /* silent */ }
      finally { if (!cancelled) setOriginalMetricsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [isPro, originalMetrics, scenario]);

  // ─── Auth check ────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(resp.data);
        // Wave 1B.2A — feature-access via `effective_capabilities`.
        const caps = resp.data.effective_capabilities || [];
        setIsPro(caps.includes('ai_advisor') || resp.data.subscription_plan === 'pro' || resp.data.subscription_plan === 'premium');
      } catch {
        navigate('/');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  // ─── Debounced canonical /calculate ────────────────────────────────────
  useEffect(() => {
    if (!isPro) return;
    if (calcTimer.current) clearTimeout(calcTimer.current);
    calcTimer.current = setTimeout(async () => {
      setMetricsLoading(true);
      try {
        const resp = await axios.post(
          `${BACKEND_URL}/api/scenario/calculate`,
          { scenario },
          { withCredentials: true }
        );
        setMetrics(resp.data.metrics);
      } catch (e) {
        // Silent fallback — leaves previous metrics visible.
      } finally {
        setMetricsLoading(false);
      }
    }, 250);
    return () => { if (calcTimer.current) clearTimeout(calcTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario, isPro]);

  // ─── Debounced AI Investment Score (existing behaviour preserved) ──────
  const [aiScoring, setAiScoring] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const aiTimer = useRef(null);
  useEffect(() => {
    if (!isPro) return;
    if (aiTimer.current) clearTimeout(aiTimer.current);
    aiTimer.current = setTimeout(async () => {
      setAiLoading(true);
      try {
        const resp = await axios.post(`${BACKEND_URL}/api/deals/simulate-analysis`, {
          purchase_price: scenario.purchase_price,
          annual_income: scenario.annual_income,
          down_payment_percent: scenario.down_payment_percent,
          interest_rate: scenario.interest_rate,
          loan_term_years: scenario.loan_term_years,
          vacancy_rate: scenario.vacancy_rate,
          property_management_percent: scenario.property_management_percent,
          annual_property_tax: scenario.annual_property_tax,
          annual_insurance: scenario.annual_insurance,
          monthly_maintenance: scenario.monthly_maintenance,
          num_units: scenario.num_units,
          property_address: scenario.property_address || '',
        }, { withCredentials: true });
        setAiScoring(resp.data);
      } catch { /* ignore */ }
      setAiLoading(false);
    }, 400);
    return () => { if (aiTimer.current) clearTimeout(aiTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario.purchase_price, scenario.interest_rate, scenario.annual_income, scenario.down_payment_percent, isPro]);

  // ─── Handlers ──────────────────────────────────────────────────────────
  const patchScenario = useCallback((patch) => {
    setScenario((s) => ({ ...s, ...patch }));
    setSolverResult(null);
  }, []);

  const toggleLock = (key) => setLocks((l) => ({ ...l, [key]: !l[key] }));

  const handleReset = () => {
    if (!originalDealRef.current) return;
    setScenario(cloneScenario(originalDealRef.current));
    setSolverResult(null);
    setLocks({ purchase_price: false, interest_rate: false, rent: false, down_payment: false });
    toast.success(t('scenario.resetSuccess'));
  };

  const handleSolve = async () => {
    if (locks[solveFor]) {
      toast.error(t('scenario.cannotSolveLocked'));
      return;
    }
    setSolving(true);
    setSolverResult(null);
    try {
      const lockedList = Object.entries(locks).filter(([, v]) => v).map(([k]) => k);
      const resp = await axios.post(
        `${BACKEND_URL}/api/scenario/solve`,
        { scenario, solve_for: solveFor, target_monthly_cash_flow: targetCashFlow, locked: lockedList },
        { withCredentials: true }
      );
      setSolverResult(resp.data);
    } catch (e) {
      toast.error(t('scenario.targetNotReachable'));
    } finally {
      setSolving(false);
    }
  };

  const handleApplyResult = () => {
    if (!solverResult || solverResult.status !== 'solved') return;
    const v = solverResult.solved_value;
    const map = {
      purchase_price: { purchase_price: v },
      rent: { annual_income: v },
      down_payment: { down_payment_percent: v },
      interest_rate: { interest_rate: v },
    };
    patchScenario(map[solverResult.solve_for] || {});
    toast.success(t('scenario.resultApplied'));
  };

  // Derived: mortgage amount + LTV
  const mortgageAmount = useMemo(
    () => Math.max(0, scenario.purchase_price * (1 - scenario.down_payment_percent / 100)),
    [scenario.purchase_price, scenario.down_payment_percent]
  );
  const ltv = 100 - scenario.down_payment_percent;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // ─── Pro gate ─────────────────────────────────────────────────────────
  if (!isPro) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] text-foreground transition-colors duration-300">
        <nav className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex justify-between items-center">
            <Link to="/dashboard"><RendPlexLogo size="md" /></Link>
            <div className="flex items-center gap-3">
              <DarkModeToggle />
              <LanguageSwitcher variant="minimal" />
              <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-slate-600 dark:text-slate-300">
                <ArrowLeft className="w-4 h-4 mr-2" />{t('nav.backToDashboard')}
              </Button>
            </div>
          </div>
        </nav>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-primary font-bold text-3xl text-slate-900 dark:text-slate-100 mb-4">{t('scenario.proFeature')}</h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">{t('scenario.proFeatureDesc')}</p>
          <Button onClick={() => navigate('/pricing')} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-6 text-lg rounded-2xl">
            <Sparkles className="w-5 h-5 mr-2" />{t('dashboard.upgradeToPro')}
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  // ─── Main Pro view ─────────────────────────────────────────────────────
  const mcf = metrics?.monthly_cash_flow ?? 0;
  const roi = metrics?.roi ?? 0;
  const capRate = metrics?.cap_rate ?? 0;
  const dscr = metrics?.dscr;

  return (
    <div data-testid="scenario-analyzer-page" className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14]">
      {/* Nav */}
      <nav className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex justify-between items-center">
          <Link to="/dashboard"><RendPlexLogo size="md" /></Link>
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full text-xs font-bold">PRO</span>
            <DarkModeToggle />
            <LanguageSwitcher variant="minimal" />
            <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-slate-600 dark:text-slate-300">
              <ArrowLeft className="w-4 h-4 mr-2" />{t('nav.backToDashboard')}
            </Button>
            <Button data-testid="new-analysis-button-pro" onClick={() => navigate('/analyze')} size="sm"
              className="bg-[#12355B] hover:bg-[#1565C0] text-white dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#0F172A] font-semibold">
              <Plus className="w-4 h-4 mr-1.5" />{t('dashboard.newAnalysis')}
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-primary font-bold text-4xl text-slate-900 dark:text-slate-100 mb-1">{t('scenario.title')}</h1>
            <p className="text-slate-600 dark:text-slate-400">{t('scenario.subtitle')}</p>
          </div>
          <Button
            data-testid="reset-to-original-btn"
            onClick={handleReset}
            variant="outline"
            className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200"
          >
            <RotateCcw className="w-4 h-4 mr-2" />{t('scenario.resetToOriginal')}
          </Button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div data-testid="kpi-monthly-cash-flow" className={`rounded-xl p-6 text-center text-white shadow-lg ${
            mcf >= 500 ? 'bg-gradient-to-br from-emerald-500 to-green-600'
              : mcf >= 0 ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
              : 'bg-gradient-to-br from-red-500 to-rose-600'
          }`}>
            <div className="flex items-center justify-center gap-2 mb-1">
              {mcf >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
              <span className="text-xs font-medium opacity-90">{t('landing.monthlyCashFlow')}</span>
              {metricsLoading && <Loader2 className="w-3 h-3 animate-spin opacity-70" />}
            </div>
            <div className="text-3xl md:text-4xl font-primary font-black">
              <AnimatedNumber value={mcf} prefix={mcf >= 0 ? '+$' : '-$'} className="tabular-nums" />
            </div>
            <p className="text-xs opacity-75 mt-1">{t('common.perMonth')}</p>
          </div>
          <div data-testid="kpi-roi" className="bg-white dark:bg-[#131824] rounded-xl p-6 text-center shadow-sm border border-slate-200 dark:border-slate-800">
            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('landing.roi')}</span>
            <div className={`text-3xl font-primary font-black mt-1 ${
              roi >= 10 ? 'text-emerald-600 dark:text-emerald-400' : roi >= 0 ? 'text-slate-900 dark:text-slate-100' : 'text-red-600 dark:text-red-400'
            }`}><AnimatedNumber value={Math.abs(roi)} suffix="%" decimals={1} /></div>
            <p className="text-[10px] text-slate-400 mt-1">{t('scenario.cashOnCash')}</p>
          </div>
          <div data-testid="kpi-cap-rate" className="bg-white dark:bg-[#131824] rounded-xl p-6 text-center shadow-sm border border-slate-200 dark:border-slate-800">
            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('analyze.capRate')}</span>
            <div className={`text-3xl font-primary font-black mt-1 ${
              capRate >= 6 ? 'text-emerald-600 dark:text-emerald-400' : capRate >= 4 ? 'text-slate-900 dark:text-slate-100' : 'text-amber-600 dark:text-amber-400'
            }`}><AnimatedNumber value={capRate} suffix="%" decimals={2} /></div>
            <p className="text-[10px] text-slate-400 mt-1">{t('scenario.netOperatingIncome')}</p>
          </div>
          <div data-testid="kpi-dscr" className="bg-white dark:bg-[#131824] rounded-xl p-6 text-center shadow-sm border border-slate-200 dark:border-slate-800">
            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('scenario.dscr')}</span>
            <div className={`text-3xl font-primary font-black mt-1 tabular-nums ${
              dscr === null || dscr === undefined ? 'text-slate-400' : dscr >= 1.2 ? 'text-emerald-600 dark:text-emerald-400' : dscr >= 1 ? 'text-slate-900 dark:text-slate-100' : 'text-red-600 dark:text-red-400'
            }`}>{dscr === null || dscr === undefined ? '—' : dscr.toFixed(2)}</div>
            <p className="text-[10px] text-slate-400 mt-1">{t('deal.mortgagePayment')}</p>
          </div>
        </div>

        {/* AI Investment Score (existing, unchanged behaviour) */}
        {aiScoring && (
          <div data-testid="live-ai-score" className="mb-6 bg-white dark:bg-[#131824] rounded-2xl border border-slate-200 dark:border-slate-800 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <AIAvatar size={22} label={t('aiEngine.title')} />
                <h3 className="font-primary font-bold text-sm text-[#12355B] dark:text-white">{t('aiEngine.title')}</h3>
                {aiLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-400" />}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-black" style={{
                  color: (aiScoring.scores?.overall || 0) >= 70 ? '#45C04A' : (aiScoring.scores?.overall || 0) >= 50 ? '#F59E0B' : '#EF4444'
                }}>{aiScoring.scores?.overall || 0}</span>
                <span className="text-xs text-slate-400">/100</span>
              </div>
            </div>
            {aiScoring.scenarios?.stress_test && (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Shield className="w-3 h-3" />
                <span>{t('aiEngine.scStress')}: ${aiScoring.scenarios.stress_test.monthly_cash_flow?.toLocaleString()}/mo</span>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Adjust Variables */}
          <div className="lg:col-span-2 bg-white dark:bg-[#131824] rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
            <h2 className="font-primary font-bold text-xl text-slate-900 dark:text-slate-100 mb-6">{t('scenario.adjustVariables')}</h2>

            <VariableRow
              label={t('scenario.purchasePrice')}
              value={scenario.purchase_price}
              onChange={(v) => patchScenario({ purchase_price: v })}
              slider={{ min: 100000, max: 2000000, step: 5000 }}
              prefix="$" step={1000}
              locked={locks.purchase_price}
              onLockToggle={() => toggleLock('purchase_price')}
              inputTestId="input-purchase-price" sliderTestId="slider-purchase-price" lockTestId="lock-purchase-price"
              extra={<><span className="mr-3">{t('scenario.mortgageAmount')}: <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">${fmt(mortgageAmount)}</span></span><span>{t('scenario.ltv')}: <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">{fmtPct(ltv, 0)}%</span></span></>}
            />

            <VariableRow
              label={t('scenario.interestRate')}
              value={scenario.interest_rate}
              onChange={(v) => patchScenario({ interest_rate: v })}
              slider={{ min: 3, max: 10, step: 0.1 }}
              suffix="%" step={0.05}
              locked={locks.interest_rate}
              onLockToggle={() => toggleLock('interest_rate')}
              inputTestId="input-interest-rate" sliderTestId="slider-interest-rate" lockTestId="lock-interest-rate"
            />

            <VariableRow
              label={t('scenario.annualRent')}
              value={scenario.annual_income}
              onChange={(v) => patchScenario({ annual_income: v })}
              slider={{ min: 12000, max: 240000, step: 1200 }}
              prefix="$" suffix="/yr" step={120}
              locked={locks.rent}
              onLockToggle={() => toggleLock('rent')}
              inputTestId="input-annual-rent" sliderTestId="slider-annual-rent" lockTestId="lock-rent"
              extra={<span>{t('scenario.monthlyRent')}: <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">${fmt(scenario.annual_income / 12)}</span>/mo</span>}
            />

            {/* Down Payment: presets + slider + numeric */}
            <div className="mb-2">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('scenario.downPayment')}</label>
                  <LockToggle locked={locks.down_payment} onToggle={() => toggleLock('down_payment')} label={t('scenario.downPayment')} testId="lock-down-payment" />
                </div>
                <span className="text-xs text-slate-500 dark:text-slate-400">${fmt(scenario.purchase_price * scenario.down_payment_percent / 100)}</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {DOWN_PAYMENT_PRESETS.map((p) => (
                  <button
                    key={p}
                    data-testid={`dp-preset-${p}`}
                    type="button"
                    onClick={() => patchScenario({ down_payment_percent: p })}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      scenario.down_payment_percent === p
                        ? 'bg-[#12355B] dark:bg-[#45C04A] text-white dark:text-[#0F172A]'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    {p}%
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_180px] gap-3 items-center">
                <Slider
                  data-testid="slider-down-payment"
                  value={[scenario.down_payment_percent]}
                  onValueChange={(v) => patchScenario({ down_payment_percent: v[0] })}
                  min={5} max={50} step={1}
                />
                <NumericField testId="input-down-payment" value={scenario.down_payment_percent}
                              onChange={(v) => patchScenario({ down_payment_percent: v })}
                              suffix="%" step={0.5} />
              </div>
            </div>
          </div>

          {/* Target Solver panel */}
          <div className="lg:col-span-1">
            <div data-testid="target-solver-panel" className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl p-6 text-white shadow-lg sticky top-24">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-5 h-5" />
                <h3 className="font-primary font-bold text-lg">{t('scenario.targetSolver')}</h3>
              </div>
              <p className="text-xs text-white/70 mb-4">{t('scenario.targetSolverSubtitle')}</p>

              {/* Target Cash Flow */}
              <label className="text-xs text-white/80 mb-2 block uppercase tracking-wider">{t('scenario.targetCashFlow')}</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {TARGET_CASH_FLOW_PRESETS.map((p) => (
                  <button
                    key={p}
                    data-testid={`target-preset-${p}`}
                    type="button"
                    onClick={() => setTargetCashFlow(p)}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      targetCashFlow === p
                        ? 'bg-white text-violet-700'
                        : 'bg-white/15 text-white hover:bg-white/25'
                    }`}
                  >
                    {p === 0 ? t('scenario.targetPreset0') : `+$${p}/mo`}
                  </button>
                ))}
              </div>
              <div className="relative mb-5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                <input
                  data-testid="input-target-cash-flow"
                  type="number"
                  value={targetCashFlow}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const n = raw === '' ? 0 : Number(raw);
                    if (Number.isFinite(n)) setTargetCashFlow(n);
                  }}
                  className="w-full pl-7 pr-14 bg-white text-slate-900 border-0 h-10 rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-white/50"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">/mo</span>
              </div>

              {/* Solve for */}
              <label className="text-xs text-white/80 mb-2 block uppercase tracking-wider">{t('scenario.solveFor')}</label>
              <div className="grid grid-cols-2 gap-1.5 mb-4">
                {SOLVE_FOR_OPTIONS.map((k) => {
                  const labelKey = k === 'purchase_price' ? 'scenario.maxPurchasePrice'
                    : k === 'rent' ? 'scenario.requiredRent'
                    : k === 'down_payment' ? 'scenario.requiredDownPayment'
                    : 'scenario.requiredInterestRate';
                  const lockedKey = k === 'rent' ? 'rent' : k;
                  const isLocked = locks[lockedKey];
                  const isSelected = solveFor === k;
                  return (
                    <button
                      key={k}
                      data-testid={`solve-for-${k}`}
                      type="button"
                      disabled={isLocked}
                      onClick={() => { setSolveFor(k); setSolverResult(null); }}
                      className={`text-left px-3 py-2 rounded-lg text-[11px] font-semibold transition-colors ${
                        isLocked
                          ? 'bg-white/10 text-white/40 cursor-not-allowed'
                          : isSelected
                            ? 'bg-white text-violet-700'
                            : 'bg-white/15 text-white hover:bg-white/25'
                      }`}
                    >
                      {t(labelKey)}
                      {isLocked && <span className="block text-[9px] opacity-70">{t('scenario.locked')}</span>}
                    </button>
                  );
                })}
              </div>

              <Button
                data-testid="solve-btn"
                onClick={handleSolve}
                disabled={solving || locks[solveFor]}
                className="w-full bg-white text-violet-700 hover:bg-white/90 font-bold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {solving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('scenario.solving')}</>) : (<><Calculator className="w-4 h-4 mr-2" />{t('scenario.solve')}</>)}
              </Button>
            </div>
          </div>
        </div>

        {/* Solver result */}
        <SolverResult
          result={solverResult}
          solveFor={solveFor}
          currentValue={
            solveFor === 'purchase_price' ? scenario.purchase_price
            : solveFor === 'rent' ? scenario.annual_income
            : solveFor === 'down_payment' ? scenario.down_payment_percent
            : scenario.interest_rate
          }
          onApply={handleApplyResult}
        />

        {/* P1A: Financial Impact (Original → Current → Delta) */}
        <FinancialImpact
          original={originalMetrics}
          current={metrics}
          loading={originalMetricsLoading || metricsLoading}
        />

        {/* P1B: Best Combination — Make This Deal Work */}
        <BestCombination
          scenario={scenario}
          locks={{
            purchase_price: locks.purchase_price,
            rent: locks.rent,
            down_payment: locks.down_payment,
            interest_rate: locks.interest_rate,
          }}
          targetCashFlow={targetCashFlow}
          onApply={(patch) => { patchScenario(patch); }}
        />

        {/* P1A: Break-Even Analysis */}
        <BreakEvenPanel scenario={scenario} locks={locks} />

        {/* P1C: Sensitivity Analysis */}
        <SensitivityBars scenario={scenario} />

        {/* P3: Decision Intelligence (tabs: Offer Price · Financing · Constraints · Summary) */}
        <DecisionIntelligence
          scenario={scenario}
          onApply={(patch) => { patchScenario(patch); }}
        />

        {/* P1A: Scenario A/B/C */}
        <ScenarioSlots
          userId={user?.user_id || user?.id}
          scenario={scenario}
          originalDeal={originalDealRef.current}
          originalMetrics={originalMetrics}
          currentMetrics={metrics}
          onLoadScenario={(sc) => { setScenario(sc); setSolverResult(null); }}
        />
      </div>

      <Footer />
    </div>
  );
}
